const DRAFT_PREFIX = "bmsd:form-draft:v1:";
const DRAFT_VERSION = 1;
const MAX_DRAFT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const UNSAFE_FIELD_PATTERN =
  /(password|passcode|otp|one.?time|token|secret|cvv|card.?number|authorization)/i;

const getInputType = (field) =>
  String(field.getAttribute?.("type") || field.type || "text").toLowerCase();

export const isUnsafeDraftField = (field) => {
  const type = getInputType(field);
  if (["password", "file", "submit", "button", "reset", "image"].includes(type)) {
    return true;
  }

  if (field.dataset?.draftDisabled === "true") return true;

  const autocomplete = String(field.getAttribute?.("autocomplete") || "");
  const identity = [
    field.name,
    field.id,
    field.getAttribute?.("aria-label"),
    field.getAttribute?.("placeholder"),
    autocomplete,
  ]
    .filter(Boolean)
    .join(" ");

  return UNSAFE_FIELD_PATTERN.test(identity);
};

export const getDraftableFields = (form) =>
  Array.from(
    form?.elements || form?.querySelectorAll?.("input, select, textarea") || [],
  ).filter((field) => {
    const tag = String(field.tagName || "").toLowerCase();
    if (!["input", "select", "textarea"].includes(tag)) return false;
    if (field.closest?.('[data-draft-disabled="true"]')) return false;

    // A page-level draft scope owns only loose controls. Native forms nested
    // inside it are saved independently, preventing duplicate or conflicting
    // draft values.
    if (String(form?.tagName || "").toLowerCase() !== "form") {
      if (field.closest?.("form")) return false;
      const nestedScope = field.closest?.('[data-draft-scope="true"]');
      if (nestedScope && nestedScope !== form) return false;
    }

    return !isUnsafeDraftField(field);
  });

const fieldIdentity = (field, index, fields) => {
  if (field.dataset?.draftField) {
    return { kind: "draftField", value: field.dataset.draftField, occurrence: 0 };
  }
  if (field.id) return { kind: "id", value: field.id, occurrence: 0 };
  if (field.name) {
    const occurrence = fields
      .slice(0, index)
      .filter((candidate) => candidate.name === field.name).length;
    return { kind: "name", value: field.name, occurrence };
  }
  return { kind: "index", value: index, occurrence: 0 };
};

const readField = (field, index, fields) => {
  const tag = String(field.tagName || "").toLowerCase();
  const type = getInputType(field);
  const identity = fieldIdentity(field, index, fields);

  if (tag === "select" && field.multiple) {
    return {
      identity,
      tag,
      type,
      value: Array.from(field.selectedOptions).map((option) => option.value),
    };
  }

  if (type === "checkbox" || type === "radio") {
    return { identity, tag, type, value: field.value, checked: field.checked };
  }

  return { identity, tag, type, value: field.value ?? "" };
};

export const serializeFormDraft = (form, metadata = {}) => {
  const fields = getDraftableFields(form);
  return {
    version: DRAFT_VERSION,
    updatedAt: new Date().toISOString(),
    ...metadata,
    fields: fields.map((field, index) => readField(field, index, fields)),
  };
};

const setNativeProperty = (field, property, value) => {
  let prototype = Object.getPrototypeOf(field);
  while (prototype) {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, property);
    if (descriptor?.set) {
      descriptor.set.call(field, value);
      return;
    }
    prototype = Object.getPrototypeOf(prototype);
  }
  field[property] = value;
};

const findField = (identity, fields) => {
  if (!identity) return null;
  if (identity.kind === "index") return fields[Number(identity.value)] || null;

  const matches = fields.filter((field) => {
    if (identity.kind === "id") return field.id === identity.value;
    if (identity.kind === "name") return field.name === identity.value;
    return field.dataset?.draftField === identity.value;
  });
  return matches[identity.occurrence || 0] || null;
};

const notifyFieldChanged = (field) => {
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
};

export const applyFormDraft = (form, draft) => {
  if (!form || !draft || draft.version !== DRAFT_VERSION) return 0;

  const fields = getDraftableFields(form);
  let restored = 0;

  draft.fields.forEach((savedField) => {
    const field = findField(savedField.identity, fields);
    if (!field || field.disabled || field.readOnly) return;

    if (savedField.tag === "select" && field.multiple) {
      const values = new Set(Array.isArray(savedField.value) ? savedField.value : []);
      Array.from(field.options).forEach((option) => {
        option.selected = values.has(option.value);
      });
    } else if (savedField.type === "checkbox" || savedField.type === "radio") {
      setNativeProperty(field, "checked", Boolean(savedField.checked));
    } else {
      setNativeProperty(field, "value", savedField.value ?? "");
    }

    notifyFieldChanged(field);
    restored += 1;
  });

  return restored;
};

const slug = (value, fallback = "form") =>
  String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || fallback;

export const getFormDraftLabel = (form) => {
  const explicit = form?.dataset?.draftLabel || form?.getAttribute?.("aria-label");
  if (explicit) return String(explicit).trim();

  const ownHeading = form?.querySelector?.("h1, legend");
  if (ownHeading?.textContent?.trim()) return ownHeading.textContent.trim();

  const container = form?.closest?.('[role="dialog"], [aria-modal="true"], .fixed');
  const nearbyHeading = container?.querySelector?.("h1, h2, h3, legend");
  if (nearbyHeading?.textContent?.trim()) return nearbyHeading.textContent.trim();

  const pageContainer = form?.closest?.("main, article, section, .crm-content");
  const pageHeading = pageContainer?.querySelector?.("h1, h2, h3");
  if (pageHeading?.textContent?.trim()) return pageHeading.textContent.trim();

  const sectionHeading = form?.querySelector?.("h2, h3");
  if (sectionHeading?.textContent?.trim()) return sectionHeading.textContent.trim();

  return "Admin form";
};

export const getFormDraftKey = ({ form, pathname, userScope, formIndex = 0 }) => {
  const explicit = form?.dataset?.draftKey || form?.id || form?.getAttribute?.("name");
  const label = getFormDraftLabel(form);
  const discriminator = explicit || `${slug(label)}-${formIndex}`;
  return `${DRAFT_PREFIX}${encodeURIComponent(userScope || "user")}:${encodeURIComponent(
    pathname || "/",
  )}:${encodeURIComponent(discriminator)}`;
};

export const readFormDraft = (key, storage = window.localStorage) => {
  try {
    const parsed = JSON.parse(storage.getItem(key) || "null");
    if (!parsed || parsed.version !== DRAFT_VERSION || !Array.isArray(parsed.fields)) {
      return null;
    }
    const updatedAt = new Date(parsed.updatedAt).getTime();
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > MAX_DRAFT_AGE_MS) {
      storage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writeFormDraft = (key, draft, storage = window.localStorage) => {
  try {
    storage.setItem(key, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
};

export const removeFormDraft = (key, storage = window.localStorage) => {
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be unavailable in private browsing or hardened environments.
  }
};

export const isFormDraftEmpty = (draft) =>
  !draft?.fields?.some((field) => {
    if (field.type === "checkbox" || field.type === "radio") return field.checked;
    if (Array.isArray(field.value)) return field.value.some(Boolean);
    return String(field.value ?? "").trim() !== "";
  });

export const FORM_DRAFT_PREFIX = DRAFT_PREFIX;
